/**
 * ActivityStats - Dashboard statistics cards
 */

import React from 'react';
import { BureauStats } from '../types/bureau-activity.types';
import {
  UsersIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

interface ActivityStatsProps {
  stats: BureauStats | null;
  loading: boolean;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, subValue, color }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
        {subValue && (
          <div className="text-xs text-gray-400 mt-1">{subValue}</div>
        )}
      </div>
    </div>
  </div>
);

export const ActivityStats: React.FC<ActivityStatsProps> = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-lg" />
              <div>
                <div className="h-7 w-16 bg-gray-200 rounded mb-1" />
                <div className="h-4 w-24 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <StatCard
        icon={<UsersIcon className="w-5 h-5 text-blue-600" />}
        label="Utilisateurs actifs"
        value={stats.activeUsers}
        subValue={`sur ${stats.totalUsers} total`}
        color="bg-blue-100"
      />
      <StatCard
        icon={<ChartBarIcon className="w-5 h-5 text-green-600" />}
        label="Total requêtes"
        value={stats.totalRequests.toLocaleString('fr-BE')}
        subValue={`${stats.totalPostRequests.toLocaleString('fr-BE')} POST`}
        color="bg-green-100"
      />
      <StatCard
        icon={<ClockIcon className="w-5 h-5 text-purple-600" />}
        label="Heures moy./jour"
        value={stats.avgDailyHours}
        color="bg-purple-100"
      />
      <StatCard
        icon={<CalendarDaysIcon className="w-5 h-5 text-indigo-600" />}
        label="Jours avec activité"
        value={stats.totalDays}
        color="bg-indigo-100"
      />
      <StatCard
        icon={<DocumentTextIcon className="w-5 h-5 text-cyan-600" />}
        label="Req. par utilisateur"
        value={stats.activeUsers > 0 ? Math.round(stats.totalRequests / stats.activeUsers).toLocaleString('fr-BE') : 0}
        subValue="moyenne"
        color="bg-cyan-100"
      />
      <StatCard
        icon={<ExclamationTriangleIcon className="w-5 h-5 text-red-600" />}
        label="Erreurs"
        value={stats.totalErrors}
        subValue={stats.totalRequests > 0 ? `${((stats.totalErrors / stats.totalRequests) * 100).toFixed(2)}%` : '0%'}
        color="bg-red-100"
      />
    </div>
  );
};
